/**
 * How the game tab and the phone page reach each other.
 *
 * - Relay: a WebSocket room on the hosted relay (`relay/`), used whenever the
 *   build sets `VITE_RELAY_URL`. Works from anywhere, mobile data included.
 * - Dev: Vite's own dev WebSocket (`npm run dev` with no relay configured).
 *
 * Both carry the same three named messages: `walkhome:state`,
 * `walkhome:event` (game → phone) and `walkhome:phone` (phone → game).
 */

export type ChannelEvent = 'walkhome:state' | 'walkhome:event' | 'walkhome:phone';

export interface Channel {
  readonly kind: 'relay' | 'dev';
  /** State updates per second this channel should be sent. */
  readonly stateHz: number;
  send(event: ChannelEvent, data: unknown): void;
  on(event: ChannelEvent, handler: (data: never) => void): void;
}

const RELAY_URL = (import.meta.env.VITE_RELAY_URL as string | undefined)?.replace(/\/+$/, '') || '';

/** Room codes: lowercase letters and digits (the relay accepts 6–16 of them). */
export function newRoomCode(): string {
  const bytes = new Uint8Array(10);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => 'abcdefghijkmnpqrstuvwxyz23456789'[b % 32]).join('');
}

export function validRoomCode(code: string | null): code is string {
  return !!code && /^[a-z0-9]{6,16}$/.test(code);
}

/** Pick the transport for this build. `room` is required for the relay. */
export function openChannel(role: 'game' | 'phone', room: string | null): Channel | null {
  if (RELAY_URL) return validRoomCode(room) ? new RelayChannel(RELAY_URL, room, role) : null;
  if (import.meta.hot) return new DevChannel(import.meta.hot);
  return null;
}

export const relayConfigured = !!RELAY_URL;

class DevChannel implements Channel {
  readonly kind = 'dev';
  readonly stateHz = 10;
  constructor(private readonly hot: NonNullable<ImportMeta['hot']>) {}
  send(event: ChannelEvent, data: unknown): void {
    this.hot.send(event, data);
  }
  on(event: ChannelEvent, handler: (data: never) => void): void {
    this.hot.on(event, handler as (data: unknown) => void);
  }
}

/** One WebSocket to the relay room, reconnecting with backoff when it drops. */
class RelayChannel implements Channel {
  readonly kind = 'relay';
  readonly stateHz = 7;
  private ws: WebSocket | null = null;
  private retry = 0;
  private readonly handlers = new Map<string, ((data: never) => void)[]>();

  constructor(
    private readonly base: string,
    private readonly room: string,
    private readonly role: 'game' | 'phone',
  ) {
    this.connect();
    // Phones lock and wake: reconnect straight away when the page comes back.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && (!this.ws || this.ws.readyState > WebSocket.OPEN)) {
        this.retry = 0;
        this.connect();
      }
    });
  }

  private connect(): void {
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) return;
    let ws: WebSocket;
    try {
      ws = new WebSocket(`${this.base}/room/${this.room}?role=${this.role}`);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
    };
    ws.onmessage = (m) => {
      if (typeof m.data !== 'string') return;
      let msg: { e?: string; d?: unknown };
      try {
        msg = JSON.parse(m.data);
      } catch {
        return;
      }
      if (!msg.e) return;
      for (const h of this.handlers.get(msg.e) ?? []) h(msg.d as never);
    };
    ws.onclose = () => {
      if (this.ws === ws) this.ws = null;
      this.scheduleReconnect();
    };
    ws.onerror = () => ws.close();
  }

  private scheduleReconnect(): void {
    const delay = Math.min(15000, 500 * 2 ** this.retry) * (0.75 + Math.random() * 0.5);
    this.retry += 1;
    setTimeout(() => this.connect(), delay);
  }

  send(event: ChannelEvent, data: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ e: event, d: data }));
  }

  on(event: ChannelEvent, handler: (data: never) => void): void {
    const list = this.handlers.get(event) ?? [];
    list.push(handler);
    this.handlers.set(event, list);
  }
}
